import axios from "axios";
import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useLocation } from "react-router-dom";
import Select from "react-select";
import PhoneInput from "react-phone-input-2";
import logo from "../../../src/assets/Aara_logo.png";

const EnrollForm = () => {
  const predefinedCountries = [
    { value: "USA", label: "USA" },
    { value: "UK", label: "UK" },
    { value: "Canada", label: "Canada" },
    { value: "Australia", label: "Australia" },
    { value: "New Zealand", label: "New Zealand" },
    { value: "Dubai", label: "Dubai" },
    { value: "Ireland", label: "Ireland" },
    { value: "Germany", label: "Germany" },
    { value: "France", label: "France" },
    { value: "Sweden", label: "Sweden" },
    { value: "Singapore", label: "Singapore" },
    { value: "Other", label: "Other" },
  ];

  const location = useLocation();
  const [leadId, setLeadId] = useState();
  const [isAuthorized, setIsAuthorized] = useState(null);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  // Validation rules
  const validateField = (name, value) => {
    switch (name) {
      case "alternate_phone":
        if (value && value.length < 10) {
          return "Phone number must be at least 10 digits";
        }
        if (value && !/^\d+$/.test(value.replace(/[\s\-\(\)\+]/g, ""))) {
          return "Please enter a valid phone number";
        }
        return "";

      case "parent_email":
        if (value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
          return "Please enter a valid email address";
        }
        return "";

      case "residence_city_state":
        if (!value || value.trim().length < 2) {
          return "Please enter your city/state/country";
        }
        return "";

      case "intakeMonth":
        if (!value) {
          return "Please select intake month";
        }
        return "";

      case "intakeYear":
        if (!value) {
          return "Please select intake year";
        }
        return "";

      case "intended_degree":
        if (!value) {
          return "Please select intended degree";
        }
        return "";

      case "intended_course":
        if (!value || value.trim().length < 2) {
          return "Please enter intended course";
        }
        return "";

      case "countries_for_abroad_studies":
        if (!value || value.length === 0) {
          return "Please select at least one country";
        }
        return "";

      case "other_countries_abroad":
        if (
          formData.countries_for_abroad_studies?.includes("Others") &&
          (!value || value.trim().length < 2)
        ) {
          return "Please specify other countries";
        }
        return "";

      default:
        return "";
    }
  };

  // Validate all fields
  const validateForm = () => {
    const newErrors = {};
    const requiredFields = [
      "residence_city_state",
      "intakeMonth",
      "intakeYear",
      "intended_degree",
      "intended_course",
      "countries_for_abroad_studies",
    ];

    requiredFields.forEach((field) => {
      const error = validateField(field, formData[field]);
      if (error) {
        newErrors[field] = error;
      }
    });

    // Validate optional fields if they have values
    ["alternate_phone", "parent_email", "other_countries_abroad"].forEach(
      (field) => {
        const error = validateField(field, formData[field]);
        if (error) {
          newErrors[field] = error;
        }
      }
    );

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const getData = async () => {
    const id = location.pathname.split("/")[2];
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/lead/api/lead/${id}/`,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      console.log("API Response:", res.data);

      const lead = res.data.data.lead || {};
      const parentInfo = res.data.data.parent_info || {};
      setFormData((prevData) => ({
        ...prevData,
        id: lead.id || "",
        lead_parent_id: lead.parent_id,
        name: parentInfo.name || lead.name || "",
        phone: parentInfo.phone || lead.phone || "",
        email: parentInfo.email || lead.email || "",
      }));

      setIsAuthorized(true);
    } catch (error) {
      console.error("Error fetching data:", error);
      setIsAuthorized(false);
      toast.error("Failed to load form data");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error("Please fix the errors before submitting");
      return;
    }

    setIsSubmitting(true);
    const id = location.pathname.split("/")[2];

    const updatedData = {
      ...formData,
      first_name: formData.name?.split(" ")[0] || "",
      last_name: formData.name?.split(" ")[1] || "",
    };

    try {
      const res = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/user/student_register/`,
        updatedData,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      toast.success("Form submitted successfully!");
      setIsSubmitted(true);
    } catch (error) {
      console.error("Error submitting form:", error);
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error("Failed to submit form. Please try again.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    getData();
  }, []);

  const handleChange = (e) => {
    if (typeof e === "string") {
      setFormData((prevData) => ({
        ...prevData,
        alternate_phone: e,
      }));

      // Clear error and validate
      setErrors((prev) => ({ ...prev, alternate_phone: "" }));
      const error = validateField("alternate_phone", e);
      if (error) {
        setErrors((prev) => ({ ...prev, alternate_phone: error }));
      }
    } else {
      const { id, value } = e.target;
      setFormData((prevData) => ({
        ...prevData,
        [id]: value,
      }));

      // Mark field as touched and validate
      setTouched((prev) => ({ ...prev, [id]: true }));
      setErrors((prev) => ({ ...prev, [id]: "" }));
      const error = validateField(id, value);
      if (error) {
        setErrors((prev) => ({ ...prev, [id]: error }));
      }
    }
  };

  const handleBlur = (fieldName) => {
    setTouched((prev) => ({ ...prev, [fieldName]: true }));
    const error = validateField(fieldName, formData[fieldName]);
    setErrors((prev) => ({ ...prev, [fieldName]: error }));
  };

  const updateIntake = (month, year) => {
    if (month && year) {
      const formattedDate = new Date(`${year}-${month}-01`)
        .toISOString()
        .split("T")[0];
      setFormData((prevState) => ({
        ...prevState,
        intakeMonth: month,
        intakeYear: year,
        intake: formattedDate,
      }));
    }
  };

  const monthOptions = [
    { value: "January", label: "January" },
    { value: "February", label: "February" },
    { value: "March", label: "March" },
    { value: "April", label: "April" },
    { value: "May", label: "May" },
    { value: "June", label: "June" },
    { value: "July", label: "July" },
    { value: "August", label: "August" },
    { value: "September", label: "September" },
    { value: "October", label: "October" },
    { value: "November", label: "November" },
    { value: "December", label: "December" },
  ];

  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 11 }, (_, index) => {
    const year = currentYear + index;
    return { value: year.toString(), label: year.toString() };
  });

  const handleMonthChange = (selectedOption) => {
    const month = selectedOption ? selectedOption.value : "";
    setFormData((prevState) => ({
      ...prevState,
      intakeMonth: month,
    }));
    updateIntake(month, formData.intakeYear);

    // Validate
    setTouched((prev) => ({ ...prev, intakeMonth: true }));
    setErrors((prev) => ({ ...prev, intakeMonth: "" }));
    const error = validateField("intakeMonth", month);
    if (error) {
      setErrors((prev) => ({ ...prev, intakeMonth: error }));
    }
  };

  const handleYearChange = (selectedOption) => {
    const year = selectedOption ? selectedOption.value : "";
    setFormData((prevState) => ({
      ...prevState,
      intakeYear: year,
    }));
    updateIntake(formData.intakeMonth, year);

    // Validate
    setTouched((prev) => ({ ...prev, intakeYear: true }));
    setErrors((prev) => ({ ...prev, intakeYear: "" }));
    const error = validateField("intakeYear", year);
    if (error) {
      setErrors((prev) => ({ ...prev, intakeYear: error }));
    }
  };

  const handleChangeCountry = (e) => {
    const { value, type } = e.target;

    setFormData((prevState) => {
      let selectedCountries = Array.isArray(
        prevState.countries_for_abroad_studies
      )
        ? [...prevState.countries_for_abroad_studies]
        : [];

      if (type === "checkbox") {
        if (value === "Others") {
          if (selectedCountries.includes(value)) {
            selectedCountries = selectedCountries.filter(
              (country) => country !== value
            );
            const newState = {
              ...prevState,
              countries_for_abroad_studies: selectedCountries,
              other_countries_abroad: "",
            };

            // Validate countries
            setTouched((prev) => ({
              ...prev,
              countries_for_abroad_studies: true,
            }));
            setErrors((prev) => ({
              ...prev,
              countries_for_abroad_studies: validateField(
                "countries_for_abroad_studies",
                selectedCountries
              ),
              other_countries_abroad: "",
            }));

            return newState;
          } else {
            selectedCountries.push(value);
            const newState = {
              ...prevState,
              countries_for_abroad_studies: selectedCountries,
            };

            // Validate countries
            setTouched((prev) => ({
              ...prev,
              countries_for_abroad_studies: true,
            }));
            setErrors((prev) => ({
              ...prev,
              countries_for_abroad_studies: validateField(
                "countries_for_abroad_studies",
                selectedCountries
              ),
            }));

            return newState;
          }
        } else {
          if (selectedCountries.includes(value)) {
            selectedCountries = selectedCountries.filter(
              (country) => country !== value
            );
          } else {
            selectedCountries.push(value);
          }
        }

        // Validate countries
        setTouched((prev) => ({ ...prev, countries_for_abroad_studies: true }));
        setErrors((prev) => ({
          ...prev,
          countries_for_abroad_studies: validateField(
            "countries_for_abroad_studies",
            selectedCountries
          ),
        }));

        return {
          ...prevState,
          countries_for_abroad_studies: selectedCountries,
        };
      } else {
        // Handle "Others" input text separately
        const newState = { ...prevState, other_countries_abroad: value };

        // Validate other countries
        setTouched((prev) => ({ ...prev, other_countries_abroad: true }));
        setErrors((prev) => ({
          ...prev,
          other_countries_abroad: validateField(
            "other_countries_abroad",
            value
          ),
        }));

        return newState;
      }
    });
  };

  // Error display component
  const ErrorMessage = ({ error }) => {
    if (!error) return null;
    return <p className="text-red-500 text-sm mt-1">{error}</p>;
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-100 p-4">
        <img
          src={logo}
          alt="Aara Consultancy Logo"
          className="h-16 sm:h-20 md:h-24 mb-4 sm:mb-6"
        />
        <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-800 mb-3 sm:mb-4 text-center">
          Thank You for Submitting!
        </h1>
        <p className="text-base sm:text-lg text-gray-600 mb-4 sm:mb-6 text-center max-w-md px-4">
          We have received your information. Please check your email for the
          login credentials to access the CRM.
        </p>
      </div>
    );
  }

  console.log(formData);

  return isAuthorized ? (
    <div className="min-h-screen bg-gray-50 py-4 sm:py-8">
      <div className="max-w-4xl mx-auto p-4 sm:p-6 bg-white shadow-md rounded-md">
        {/* Logo Section */}
        <div className="flex justify-center mb-4 sm:mb-6">
          <img
            src={logo}
            alt="Aara Consultancy Logo"
            className="h-16 sm:h-20"
          />
        </div>

        {/* Title */}
        <h1 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6 text-center">
          Enrollment Form
        </h1>

        <form
          onSubmit={(e) => handleSubmit(e)}
          className="space-y-4 sm:space-y-6"
        >
          {/* Name Field */}
          <div className="mb-4">
            <label className="block text-sm font-medium mb-2" htmlFor="name">
              Name
            </label>
            <input
              type="text"
              id="name"
              value={formData.name || ""}
              className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-300 disabled:bg-gray-200"
              disabled
            />
          </div>

          {/* Contact Number */}
          <div className="mb-4">
            <label
              className="block text-sm font-medium mb-2"
              htmlFor="contact_number"
            >
              Contact Number
            </label>
            <PhoneInput
              country={"in"}
              value={formData.phone || ""}
              inputStyle={{
                width: "100%",
                height: "40px",
                borderRadius: "6px",
                borderColor: "#d1d5db",
                paddingLeft: "50px",
                fontSize: "16px",
              }}
              dropdownStyle={{
                width: "100%",
                maxWidth: "320px",
              }}
              disabled
            />
          </div>

          {/* Alternate Contact Number */}
          <div className="mb-4">
            <label
              className="block text-sm font-medium mb-2"
              htmlFor="alternate_phone"
            >
              Alternate Contact Number
            </label>
            <PhoneInput
              country={"in"}
              value={formData.alternate_phone || ""}
              onChange={handleChange}
              onBlur={() => handleBlur("alternate_phone")}
              inputStyle={{
                width: "100%",
                height: "40px",
                borderRadius: "6px",
                borderColor: errors.alternate_phone ? "#ef4444" : "#d1d5db",
                paddingLeft: "50px",
                fontSize: "16px",
              }}
              dropdownStyle={{
                width: "100%",
                maxWidth: "320px",
              }}
            />
            <ErrorMessage error={errors.alternate_phone} />
          </div>

          {/* Email */}
          <div className="mb-4">
            <label className="block text-sm font-medium mb-2" htmlFor="email">
              Email ID
            </label>
            <input
              type="email"
              id="email"
              value={formData.email || ""}
              className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-300 disabled:bg-gray-200"
              disabled
            />
          </div>

          {/* Parent's Email */}
          <div className="mb-4">
            <label
              className="block text-sm font-medium mb-2"
              htmlFor="parent_email"
            >
              Parent's Email
            </label>
            <input
              type="email"
              id="parent_email"
              value={formData.parent_email || ""}
              onChange={handleChange}
              onBlur={() => handleBlur("parent_email")}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                errors.parent_email
                  ? "border-red-500 focus:ring-red-300"
                  : "border-gray-300 focus:ring-indigo-300"
              }`}
              placeholder="Enter parent's email address"
            />
            <ErrorMessage error={errors.parent_email} />
          </div>

          {/* City/State/Country */}
          <div className="mb-4">
            <label
              className="block text-sm font-medium mb-2"
              htmlFor="residence_city_state"
            >
              City/State/Country <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="residence_city_state"
              value={formData.residence_city_state || ""}
              onChange={handleChange}
              onBlur={() => handleBlur("residence_city_state")}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                errors.residence_city_state
                  ? "border-red-500 focus:ring-red-300"
                  : "border-gray-300 focus:ring-indigo-300"
              }`}
              placeholder="e.g., Mumbai, Maharashtra, India"
            />
            <ErrorMessage error={errors.residence_city_state} />
          </div>

          {/* Intake Section */}
          <div className="mb-4">
            <label htmlFor="intake" className="block text-sm font-medium mb-2">
              Intake you do wish to go for{" "}
              <span className="text-red-500">*</span>
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label
                  htmlFor="intakeMonth"
                  className="block text-sm font-medium mb-1"
                >
                  Month <span className="text-red-500">*</span>
                </label>
                <Select
                  name="intakeMonth"
                  options={monthOptions}
                  onChange={handleMonthChange}
                  value={monthOptions.find(
                    (option) => option.value === formData.intakeMonth
                  )}
                  isSearchable
                  isClearable
                  placeholder="Select Month"
                  className="text-sm"
                  styles={{
                    control: (base) => ({
                      ...base,
                      borderColor: errors.intakeMonth ? "#ef4444" : "#d1d5db",
                      "&:hover": {
                        borderColor: errors.intakeMonth ? "#ef4444" : "#d1d5db",
                      },
                    }),
                  }}
                />
                <ErrorMessage error={errors.intakeMonth} />
              </div>
              <div>
                <label
                  htmlFor="intakeYear"
                  className="block text-sm font-medium mb-1"
                >
                  Year <span className="text-red-500">*</span>
                </label>
                <Select
                  name="intakeYear"
                  options={yearOptions}
                  onChange={handleYearChange}
                  value={yearOptions.find(
                    (option) => option.value === formData.intakeYear
                  )}
                  isSearchable
                  isClearable
                  placeholder="Select Year"
                  className="text-sm"
                  styles={{
                    control: (base) => ({
                      ...base,
                      borderColor: errors.intakeYear ? "#ef4444" : "#d1d5db",
                      "&:hover": {
                        borderColor: errors.intakeYear ? "#ef4444" : "#d1d5db",
                      },
                    }),
                  }}
                />
                <ErrorMessage error={errors.intakeYear} />
              </div>
            </div>
          </div>

          {/* Intended Degree */}
          <div className="mb-4">
            <label
              className="block text-sm font-medium mb-2"
              htmlFor="intended_degree"
            >
              Intended Degree <span className="text-red-500">*</span>
            </label>
            <select
              id="intended_degree"
              value={formData.intended_degree || ""}
              onChange={handleChange}
              onBlur={() => handleBlur("intended_degree")}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                errors.intended_degree
                  ? "border-red-500 focus:ring-red-300"
                  : "border-gray-300 focus:ring-indigo-300"
              }`}
            >
              <option value="">Select Degree</option>
              <option value="Undergraduate">Undergraduate/Bachelors</option>
              <option value="Transfer">Transfer</option>
              <option value="PG Diploma">PG Diploma</option>
              <option value="Masters">Masters</option>
              <option value="MBA">MBA</option>
              <option value="PhD">PhD</option>
              <option value="Medical">Medical</option>
            </select>
            <ErrorMessage error={errors.intended_degree} />
          </div>

          {/* Intended Course */}
          <div className="mb-4">
            <label
              className="block text-sm font-medium mb-2"
              htmlFor="intended_course"
            >
              Intended Course <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="intended_course"
              value={formData.intended_course || ""}
              onChange={handleChange}
              onBlur={() => handleBlur("intended_course")}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                errors.intended_course
                  ? "border-red-500 focus:ring-red-300"
                  : "border-gray-300 focus:ring-indigo-300"
              }`}
              placeholder="e.g., Computer Science, Business Administration"
            />
            <ErrorMessage error={errors.intended_course} />
          </div>

          {/* Countries Section */}
          <div className="mb-6">
            <label
              htmlFor="countries_for_abroad_studies"
              className="block text-sm font-medium mb-3"
            >
              Intended Countries (Multiple select){" "}
              <span className="text-red-500">*</span>
            </label>

            {/* Country checkboxes - responsive grid */}
            <div
              className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 mb-4 p-3 border rounded-md ${
                errors.countries_for_abroad_studies
                  ? "border-red-500"
                  : "border-gray-300"
              }`}
            >
              {[
                "USA",
                "UK",
                "Australia",
                "Canada",
                "Germany",
                "India",
                "Ireland",
                "Newzealand",
                "Others",
              ].map((country) => (
                <label
                  key={country}
                  className="flex items-center space-x-2 p-2 hover:bg-gray-50 rounded"
                >
                  <input
                    type="checkbox"
                    value={country}
                    onChange={handleChangeCountry}
                    checked={
                      formData.countries_for_abroad_studies &&
                      formData.countries_for_abroad_studies.includes(country)
                    }
                    className="h-4 w-4 text-indigo-600 rounded border-gray-300 focus:ring-indigo-500"
                  />
                  <span className="text-sm">{country}</span>
                </label>
              ))}
            </div>
            <ErrorMessage error={errors.countries_for_abroad_studies} />

            {/* Other Countries Input */}
            {formData.countries_for_abroad_studies &&
              formData.countries_for_abroad_studies.includes("Others") && (
                <div className="mt-4">
                  <label
                    htmlFor="other_countries_abroad"
                    className="block text-sm font-medium mb-2"
                  >
                    Other Countries For Abroad Studies{" "}
                    <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="other_countries_abroad"
                    value={formData.other_countries_abroad || ""}
                    onChange={handleChangeCountry}
                    onBlur={() => handleBlur("other_countries_abroad")}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                      errors.other_countries_abroad
                        ? "border-red-500 focus:ring-red-300"
                        : "border-gray-300 focus:ring-indigo-300"
                    }`}
                    placeholder="Enter other countries"
                  />
                  <ErrorMessage error={errors.other_countries_abroad} />
                </div>
              )}
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isSubmitting}
            className={`w-full py-3 px-4 rounded-md font-medium transition duration-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${
              isSubmitting
                ? "bg-gray-400 cursor-not-allowed text-white"
                : "bg-indigo-600 hover:bg-indigo-700 text-white"
            }`}
          >
            {isSubmitting ? (
              <span className="flex items-center justify-center">
                <svg
                  className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                Submitting...
              </span>
            ) : (
              "Submit"
            )}
          </button>
        </form>
      </div>
    </div>
  ) : isAuthorized === false ? (
    <div className="min-h-screen flex items-center justify-center bg-gray-100 p-4">
      <div className="text-center">
        <h1 className="text-xl sm:text-2xl font-semibold text-gray-800 mb-2">
          Access Denied
        </h1>
        <p className="text-gray-600">You are not allowed to enroll</p>
      </div>
    </div>
  ) : (
    <div className="min-h-screen flex items-center justify-center bg-gray-100">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
        <p className="text-gray-600">Loading...</p>
      </div>
    </div>
  );
};

export default EnrollForm;
